import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CreditCard, CheckCircle2 } from "lucide-react";
import { createOrder, euro } from "@/lib/store";
import { Seo } from "@/components/Seo";

export default function Checkout() {
  const [sp] = useSearchParams();
  const variant = sp.get("variant");
  const name = sp.get("name") || "Prodotto";
  const price = parseInt(sp.get("price") || "0", 10);
  const storage = sp.get("storage"); const color = sp.get("color");
  const [f, setF] = useState({ first_name: "", last_name: "", phone: "", email: "", fulfillment: "RITIRO", address: "", city: "", zip: "", notes: "" });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      const data = await createOrder({ items: [{ variant_id: variant, qty: 1 }], ...f });
      setResult(data);
    } catch (e) { setErr(e.response?.data?.detail || "Errore nella creazione dell'ordine"); }
    finally { setBusy(false); }
  };

  if (!variant) return <main className="mx-auto max-w-2xl px-4 py-16 text-center text-zinc-400"><Seo title="Checkout · iPhix Scafati" noindex />Nessun prodotto selezionato. <Link to="/" className="text-[#2563EB]">Torna alla home</Link></main>;

  if (result) return (
    <main className="mx-auto max-w-2xl px-4 py-12" data-testid="checkout-success">
      <Seo title="Ordine creato · iPhix Scafati" noindex />
      <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-6 text-center">
        <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-400" />
        <h1 className="mt-3 text-2xl font-extrabold">Ordine creato!</h1>
        <p className="mt-1 text-zinc-300">Numero ordine: <span className="font-mono font-bold text-white">{result.order_number}</span></p>
      </div>
      {result.bank?.configurato ? (
        <div className="mt-6 rounded-2xl border border-amber-500/30 bg-amber-500/5 p-5">
          <h3 className="font-semibold text-amber-300">Paga con bonifico</h3>
          <div className="mt-3 grid gap-1 text-sm text-zinc-300">
            <div className="flex justify-between"><span className="text-zinc-500">Intestatario</span><span>{result.bank.intestatario}</span></div>
            <div className="flex justify-between"><span className="text-zinc-500">IBAN</span><span className="font-mono">{result.bank.iban}</span></div>
            <div className="flex justify-between"><span className="text-zinc-500">Causale</span><span className="font-mono font-bold text-white">{result.bank.causale}</span></div>
            <div className="flex justify-between"><span className="text-zinc-500">Importo</span><span className="font-bold text-white">{result.bank.importo}</span></div>
          </div>
          <p className="mt-3 text-xs text-zinc-500">{result.bank.istruzioni}</p>
        </div>
      ) : <p className="mt-6 text-center text-zinc-400">{result.bank?.note}</p>}
      <div className="mt-6 text-center"><Link to="/stato-ordine" className="rounded-full bg-[#2563EB] px-6 py-3 font-semibold text-white">Verifica stato ordine</Link></div>
    </main>
  );

  return (
    <main className="mx-auto max-w-2xl px-4 py-12">
      <Seo title="Checkout · iPhix Scafati" noindex />
      <h1 className="text-3xl font-extrabold">Checkout</h1>
      <div className="mt-4 flex items-center justify-between rounded-xl border border-white/10 bg-[#11141A] p-4">
        <div><p className="font-semibold text-white">{name}</p><p className="text-xs text-zinc-500">{[storage, color].filter(Boolean).join(" · ")}</p></div>
        <span className="text-lg font-bold text-[#2563EB]">{euro(price)}</span>
      </div>
      <form onSubmit={submit} className="mt-6 space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <input required data-testid="checkout-first-name" value={f.first_name} onChange={set("first_name")} placeholder="Nome" className="rounded-xl border border-white/10 bg-[#11141A] px-4 py-3 text-white placeholder:text-zinc-600" />
          <input required data-testid="checkout-last-name" value={f.last_name} onChange={set("last_name")} placeholder="Cognome" className="rounded-xl border border-white/10 bg-[#11141A] px-4 py-3 text-white placeholder:text-zinc-600" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <input required data-testid="checkout-phone" value={f.phone} onChange={set("phone")} placeholder="Telefono" className="rounded-xl border border-white/10 bg-[#11141A] px-4 py-3 text-white placeholder:text-zinc-600" />
          <input required type="email" data-testid="checkout-email" value={f.email} onChange={set("email")} placeholder="Email" className="rounded-xl border border-white/10 bg-[#11141A] px-4 py-3 text-white placeholder:text-zinc-600" />
        </div>
        <div className="flex gap-2">
          {["RITIRO", "SPEDIZIONE"].map((m) => <button type="button" key={m} data-testid={`fulfillment-${m}`} onClick={() => setF({ ...f, fulfillment: m })} className={`flex-1 rounded-xl border px-4 py-3 text-sm font-semibold ${f.fulfillment === m ? "border-[#2563EB] bg-[#2563EB]/20 text-white" : "border-white/10 text-zinc-300"}`}>{m === "RITIRO" ? "Ritiro in negozio" : "Spedizione"}</button>)}
        </div>
        {f.fulfillment === "SPEDIZIONE" && (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <input data-testid="checkout-address" value={f.address} onChange={set("address")} placeholder="Indirizzo" className="sm:col-span-3 rounded-xl border border-white/10 bg-[#11141A] px-4 py-3 text-white placeholder:text-zinc-600" />
            <input value={f.city} onChange={set("city")} placeholder="Città" className="rounded-xl border border-white/10 bg-[#11141A] px-4 py-3 text-white placeholder:text-zinc-600" />
            <input value={f.zip} onChange={set("zip")} placeholder="CAP" className="rounded-xl border border-white/10 bg-[#11141A] px-4 py-3 text-white placeholder:text-zinc-600" />
          </div>
        )}
        {err && <p className="text-rose-400">{err}</p>}
        <button disabled={busy} data-testid="checkout-submit" className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#2563EB] px-6 py-3.5 font-semibold text-white disabled:opacity-50"><CreditCard className="h-5 w-5" /> {busy ? "Invio…" : "Conferma ordine (bonifico)"}</button>
        <p className="text-center text-xs text-zinc-600">Pagamento con bonifico bancario. Riceverai le istruzioni con IBAN e causale. Verifica manuale del pagamento.</p>
      </form>
    </main>
  );
}
